import { useState, useEffect, useRef } from 'react';
import api from '../../hooks/useApi';
import { formatMoney } from '../../utils/format';

// Inline, single-row "type it and hit Enter" add — the fast alternative to
// the full Add Item/Add Line modals, for quoting a job the way the old
// pricing spreadsheet worked: pick the item, set qty, it's on the list,
// straight on to the next line without a modal round-trip each time.

export function QuickAddHardwareRow({ detailId, onAdded }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(1);
  const [sellPrice, setSellPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!search.trim() || selected) { setResults([]); return; }
    // Resource-category catalog rows are old rate-card-era seed data, not
    // real hardware — resource pricing comes from rate_card_bands via the
    // Resources tab, never from here, so they shouldn't be pickable as a
    // "hardware" line at all.
    api.get('product-catalog', { search }).then(r => setResults((Array.isArray(r) ? r : []).filter(p => p.category !== 'Resource'))).catch(() => setResults([]));
  }, [search, selected]);

  // min_price (from product_catalog_suppliers, e.g. a live TD SYNNEX sync)
  // is the real current cost — product_catalog.unit_cost is a stale field
  // from the original price-list seed and isn't kept in sync with anything.
  // Rounded here since supplier prices (a MIN() aggregate over REAL columns)
  // can carry float noise that would otherwise show as e.g. £99.10000000001.
  const catalogCost = (p) => Math.round((p.min_price ?? p.unit_cost ?? 0) * 100) / 100;

  const pick = (p) => {
    setSelected(p);
    setSearch(p.description);
    setResults([]);
    setSellPrice(p.sell_price != null && p.sell_price > 0 ? (Math.round(p.sell_price * 100) / 100).toFixed(2) : (Math.round(catalogCost(p) * 1.2 * 100) / 100).toFixed(2));
  };

  const reset = () => {
    setSearch(''); setSelected(null); setQty(1); setSellPrice(''); setResults([]);
    inputRef.current?.focus();
  };

  const add = async () => {
    const description = selected?.description || search.trim();
    if (!description) return;
    setSaving(true);
    try {
      await api.post(`sows/${detailId}/hardware-lines`, {
        item_description: description,
        part_number: selected?.manufacturer_part_number || null,
        quantity: parseFloat(qty) || 1,
        unit_cost: selected ? catalogCost(selected) : 0,
        unit_sell: Math.round((parseFloat(sellPrice) || 0) * 100) / 100,
        category: selected?.category || 'Hardware',
        product_catalog_id: selected?.id || null,
      });
      await onAdded();
      reset();
    } catch (e) {
      alert(e.message || 'Failed to add item');
    }
    setSaving(false);
  };

  return (
    <div className="bg-gradient-to-br from-white to-gold/5 border border-gold/25 rounded-xl shadow-sm p-2.5 mb-2 flex items-center gap-2 flex-wrap">
      <span className="flex items-center gap-1 text-[8px] font-bold text-gold uppercase tracking-wide flex-shrink-0 px-1.5 py-1 rounded-md bg-gold/10">
        ⚡ Quick add
      </span>
      <div className="relative flex-1 min-w-[220px]">
        <input ref={inputRef} value={search}
          onChange={e => { setSearch(e.target.value); setSelected(null); }}
          onKeyDown={e => { if (e.key === 'Enter' && (selected || search.trim())) add(); }}
          placeholder="Type an item — pick a match, or just type your own description…"
          className="form-input text-[9px] w-full bg-white shadow-inner" />
        {results.length > 0 && (
          <div className="absolute z-10 top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl mt-1 max-h-48 overflow-y-auto">
            {results.slice(0, 8).map(p => (
              <div key={p.id} onClick={() => pick(p)} className="px-2.5 py-1.5 text-[9px] hover:bg-gold/10 cursor-pointer border-b border-gray-50 last:border-0">
                <div className="font-semibold text-navy">{p.description}</div>
                <div className="text-[8px] text-gray-400">{p.code} · {p.category} · {p.sell_price != null ? formatMoney(p.sell_price) : formatMoney((p.unit_cost || 0) * 1.2)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <input type="number" min="1" value={qty} onChange={e => setQty(e.target.value)}
        className="form-input text-[9px] w-16 bg-white shadow-inner" placeholder="Qty" title="Quantity" />
      <input type="number" step="0.01" value={sellPrice} onChange={e => setSellPrice(e.target.value)}
        className="form-input text-[9px] w-24 bg-white shadow-inner" placeholder="Sell price" title="Sell price" />
      <button onClick={add} disabled={saving || !(selected || search.trim())}
        className="px-3 py-1.5 text-[9px] font-bold bg-navy text-white rounded-md hover:bg-navy/80 disabled:opacity-40 shadow-sm">
        {saving ? '…' : 'Add ↵'}
      </button>
      {!selected && search.trim() && results.length === 0 && (
        <div className="text-[8px] text-amber-600 w-full">No catalogue match — this will be added as a free-text item (not linked to the Product Catalog).</div>
      )}
    </div>
  );
}

export function QuickAddResourceRow({ detailId, section, onAdded }) {
  const [bands, setBands] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [days, setDays] = useState(1);
  const [activity, setActivity] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { api.get('rate-cards/active-bands').then(setBands).catch(() => setBands([])); }, []);

  const matches = search.trim() && !selected
    ? bands.filter(b => `${b.band_code} ${b.band_name}`.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8)
    : [];

  const pick = (b) => { setSelected(b); setSearch(`${b.band_code} — ${b.band_name}`); };
  const reset = () => { setSearch(''); setSelected(null); setDays(1); setActivity(''); inputRef.current?.focus(); };

  const add = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post(`sows/${detailId}/resource-lines`, {
        band_code: selected.band_code, band_name: selected.band_name, day_rate: selected.day_rate,
        days: parseFloat(days) || 1, work_package: section, description: activity.trim() || null, sort_order: 0,
      });
      await onAdded();
      reset();
    } catch (e) {
      alert(e.message || 'Failed to add resource');
    }
    setSaving(false);
  };

  return (
    <div className="bg-gradient-to-br from-white to-gold/5 border-t border-gold/25 p-2.5 flex items-center gap-2 flex-wrap">
      <span className="flex items-center gap-1 text-[8px] font-bold text-gold uppercase tracking-wide flex-shrink-0 px-1.5 py-1 rounded-md bg-gold/10">
        ⚡ Quick add
      </span>
      <div className="relative flex-1 min-w-[200px]">
        <input ref={inputRef} value={search}
          onChange={e => { setSearch(e.target.value); setSelected(null); }}
          onKeyDown={e => { if (e.key === 'Enter' && selected) add(); }}
          placeholder="Type a role/band…" className="form-input text-[9px] w-full bg-white shadow-inner" />
        {matches.length > 0 && (
          <div className="absolute z-10 top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl mt-1 max-h-48 overflow-y-auto">
            {matches.map(b => (
              <div key={b.id} onClick={() => pick(b)} className="px-2.5 py-1.5 text-[9px] hover:bg-gold/10 cursor-pointer border-b border-gray-50 last:border-0">
                <span className="font-mono font-bold text-navy">{b.band_code}</span> — {b.band_name}
                <span className="text-[8px] text-gray-400 ml-1">{formatMoney(b.day_rate)}/day</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <input value={activity} onChange={e => setActivity(e.target.value)}
        placeholder="Activity / task (optional)…" title="What this resource line is for — shows on the Mini SOW PDF for call-offs"
        className="form-input text-[9px] flex-1 min-w-[160px] bg-white shadow-inner" />
      <input type="number" min="0.5" step="0.5" value={days} onChange={e => setDays(e.target.value)}
        className="form-input text-[9px] w-16 bg-white shadow-inner" placeholder="Days" title="Days" />
      {selected && <span className="text-[8px] text-gray-400 whitespace-nowrap">{formatMoney(Math.round(selected.day_rate * (parseFloat(days) || 0) * 100) / 100)}</span>}
      <button onClick={add} disabled={saving || !selected}
        className="px-3 py-1.5 text-[9px] font-bold bg-navy text-white rounded-md hover:bg-navy/80 disabled:opacity-40 shadow-sm">
        {saving ? '…' : 'Add ↵'}
      </button>
    </div>
  );
}
